<template>
  <div class="card p-4">
    <div class="text-center process mx-auto">
      <img width="218" height="150" class="mb-4" alt="uploading" :src="uploadingImage" />

      <h5 class="mb-4">Sedang Memproses...</h5>

      <div class="text-start">
        <McProgressBar height="8px" :value="percentage" />

        <div class="d-flex align-items-center justify-content-between mt-4">
          <span class="__totalCount">
            {{ processed }} / {{ total }} data
          </span>

          <span class="__percentage">
            {{ percentage }}%
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import uploadingImage from '@/assets/images/import-device/upload-in-progress.png'
import McProgressBar from '@/components/McProgressBar'
import * as importJobsApi from '@/services/rest-api-sms/lib/import-jobs/import-jobs'

export default {
  components: { McProgressBar },

  data() {
    return {
      jobId: null,
      jobKey: null,

      total: 0,
      processed: 0,
      percentage: 0,

      ws: null,
      wsConnected: false,

      pollingInterval: null
    }
  },

  computed: {
    uploadingImage() {
      return uploadingImage
    }
  },

  async mounted() {
    this.jobId = this.$route.params.jobId

    if (!this.jobId) {
      this.$toast.error('Job tidak ditemukan')
      this.$router.replace({ name: 'Device Import Set To Customer' })
      return
    }

    await this.fetchInitialData()

    this.connectWebSocket()

    // fallback polling jika WS gagal
    setTimeout(() => {
      if (!this.wsConnected) {
        console.warn('WS gagal connect → pakai polling')
        this.startPolling()
      }
    }, 5000)
  },

  beforeDestroy() {
    this.disconnectWebSocket()
    this.stopPolling()
  },

  methods: {

    /* =======================
       FETCH DATA AWAL
    ======================= */

    async fetchInitialData() {
      try {
        const res = await importJobsApi.getJobDetail(this.jobId)

        this.total = res.data?.total_rows || 0
        this.processed = res.data?.processed_rows || 0
        this.jobKey = res.data?.job_key

        this.calculatePercentage()

        if (res.data?.status === 'completed') {
          this.redirectToResult()
        }

      } catch (error) {
        console.error('Fetch job error:', error)
      }
    },

    /* =======================
       WEBSOCKET
    ======================= */

    connectWebSocket() {

      if (!this.jobKey) {
        console.warn('jobKey belum ada')
        return
      }

      const baseUrl = process.env.VUE_APP_WS_URL
      const token = localStorage.getItem('wsToken')

      const wsUrl = token
        ? `${baseUrl}/ws?token=${token}`
        : `${baseUrl}/ws`

      console.log('WS CONNECT →', wsUrl)

      this.ws = new WebSocket(wsUrl)

      this.ws.onopen = () => {

        console.log('WS CONNECTED')

        this.wsConnected = true

        this.stopPolling()

        // delay subscribe supaya server siap
        setTimeout(() => {
          this.subscribeJob()
        }, 500)
      }

      this.ws.onmessage = this.handleWsMessage

      this.ws.onerror = (err) => {
        console.error('WS ERROR', err)
      }

      this.ws.onclose = () => {

        console.warn('WS CLOSED')

        this.wsConnected = false
        this.ws = null

        this.startPolling()
      }
    },

    subscribeJob() {

      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        console.warn('WS belum ready')
        return
      }

      const message = {
        type: 'update_tags',
        data: {
          tags: [`import-job-${this.jobKey}`]
        }
      }

      console.log('WS SUBSCRIBE →', message)

      this.ws.send(JSON.stringify(message))
    },

    handleWsMessage(event) {

      console.log('WS RAW MESSAGE →', event.data)

      let signal

      try {
        signal = JSON.parse(event.data)
      } catch (err) {
        console.warn('WS message bukan JSON')
        return
      }

      /* heartbeat */

      if (signal.type === 'PING') {

        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: 'PONG' }))
        }

        return
      }

      /* cari payload job */

      let jobData = null

      if (signal.type === `import-job-${this.jobKey}`) {
        jobData = signal.payload
      }

      if (signal.tag === `import-job-${this.jobKey}`) {
        jobData = signal.data
      }

      if (signal.data?.job_key === this.jobKey) {
        jobData = signal.data
      }

      if (!jobData) {
        return
      }

      console.log('JOB UPDATE →', jobData)

      this.updateProgress(jobData)
    },

    disconnectWebSocket() {

      if (!this.ws) return

      try {

        this.ws.onopen = null
        this.ws.onmessage = null
        this.ws.onerror = null
        this.ws.onclose = null

        if (this.ws.readyState === WebSocket.OPEN) {
          this.ws.close()
        }

      } catch (err) {
        console.warn(err)
      }

      this.ws = null
      this.wsConnected = false
    },

    /* =======================
       POLLING FALLBACK
    ======================= */

    startPolling() {

      if (this.pollingInterval) return

      console.warn('START POLLING')

      this.pollingInterval = setInterval(async () => {

        try {

          const res = await importJobsApi.getJobDetail(this.jobId)

          this.total = res.data?.total_rows || this.total
          this.processed = res.data?.processed_rows || this.processed

          this.calculatePercentage()

          if (res.data?.status === 'completed') {
            this.redirectToResult()
          }

        } catch (err) {
          console.error('Polling error', err)
        }

      }, 3000)
    },

    stopPolling() {

      if (!this.pollingInterval) return

      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    },

    /* =======================
       UPDATE PROGRESS
    ======================= */

    updateProgress(data) {

      if (!data) return

      this.total = data.total_rows || this.total
      this.processed = data.processed_rows || this.processed

      this.calculatePercentage()

      if (data.status === 'completed') {
        this.redirectToResult()
      }
    },

    calculatePercentage() {

      if (!this.total) {
        this.percentage = 0
        return
      }

      this.percentage = Math.round((this.processed / this.total) * 100)
    },

    redirectToResult() {

      this.disconnectWebSocket()
      this.stopPolling()

      this.$router.replace({
        name: 'Device Import Set To Customer Result',
        params: { jobId: this.jobId }
      })
    }
  }
}
</script>

<style scoped lang="scss">

.process {
  max-width: 300px;
}

.__totalCount {
  color: $teal-300;
}

.__percentage {
  color: $blue;
  font-size: 16px;
  font-weight: 600;
}

</style>